import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Card, CardContent } from '../../../../components/ui/Card';
import { StatutoryBaseAlert } from './StatutoryBaseAlert';
import { getGosiPanelData } from '../../../../api/Payroll/gosi';
import { getApiErrorMessage } from '../../../../utils/apiErrors';
import { appMonthNameYear } from '../../../../utils/appDateFormat';
import type {
  KsaGosiPanelData,
  GosiBranchSummary,
  GosiCoverageRow,
} from '../../../../models/payroll';

export interface KsaGosiPanelProps {
  year: number;
  month: number;
  onError: (msg: string) => void;
}

type BranchKey = 'ani' | 'oh' | 'saned';

const formatMoney = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRate = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined) return '—';
  // Drop trailing zeros: 0.09 → "9%", 0.0075 → "0.75%".
  return `${Number((rate * 100).toFixed(2))}%`;
};

const formatNationality = (nat: string | null | undefined): string => {
  if (!nat) return '—';
  return nat;
};

export const KsaGosiPanel: React.FC<KsaGosiPanelProps> = ({ year, month, onError }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<KsaGosiPanelData | null>(null);
  const [loading, setLoading] = useState(true);

  // ANI expanded by default (most-used). OH + SANED collapsed.
  const [expanded, setExpanded] = useState<Record<BranchKey, boolean>>({
    ani: true,
    oh: false,
    saned: false,
  });
  const [ratesExpanded, setRatesExpanded] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Cleared up front: `data` is written only on success, so a period change whose fetch fails
      // would otherwise re-render the PREVIOUS period's GOSI figures with `loading` back to false
      // and only a transient error banner to contradict them.
      setData(null);
      try {
        const result = await getGosiPanelData(year, month);
        if (cancelled) return;
        if (result.errors.length === 0) {
          setData(result.payload ?? null);
        } else {
          onError(getApiErrorMessage(result, t, t('common.error', 'An error occurred.')));
        }
      } catch (e) {
        if (!cancelled) {
          onError(getApiErrorMessage(e, t, t('common.error', 'An error occurred.')));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [year, month, onError, t]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-5 text-sm text-muted-foreground">
          {t('common.loading', 'Loading...')}
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const monthLabel = appMonthNameYear(new Date(data.year, data.month - 1, 1));

  const toggle = (key: BranchKey) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-4">
      {/* 🔴 CO-256 — first thing on the screen when the wage base collapsed. It sits ABOVE the
          totals deliberately: the totals below are the symptom (an ANI row reading 135.00/135.00
          instead of 720.00/720.00) and they look perfectly valid on their own. */}
      <StatutoryBaseAlert health={data.statutoryBaseHealth} />

      {/* Header + compact summary row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {t('gosi.panel.heading', 'GOSI (Social Insurance)')} · {monthLabel}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
            <Info size={12} />
            {t('gosi.panel.disclaimer.short', 'Computed from payroll. Employee shares appear on payslips; employer shares are filed via GOSI Online.')}
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">{t('gosi.panel.total_employer_obligation', 'Employer total')}:</span>
          <span className="font-semibold font-mono">SAR {formatMoney(data.totalEmployerObligation)}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">{t('payroll.complianceCenter.gosi.saudiNationals', 'Saudi')}: <span className="font-medium text-foreground">{data.saudiNationalsCount}</span></span>
          <span className="text-muted-foreground">{t('payroll.complianceCenter.gosi.totalEmployees', 'Total')}: <span className="font-medium text-foreground">{data.totalEmployeesCount}</span></span>
        </div>
      </div>

      {/* Section 1 — ANI */}
      <BranchSection
        sectionKey="ani"
        title={t('gosi.ani.title', 'GOSI ANI (Annuities): Saudi nationals only')}
        description={t(
          'gosi.ani.description',
          'Annuities branch: shared employer + employee contribution for Saudi nationals.',
        )}
        summary={data.aniSummary}
        expanded={expanded.ani}
        onToggle={() => toggle('ani')}
        renderRow={renderAniRow}
        renderHeader={renderAniHeader}
        showEmployeeColumn
        t={t}
      />

      {/* Section 2 — OH */}
      <BranchSection
        sectionKey="oh"
        title={t('gosi.oh.title', 'GOSI OH (Occupational Hazards): all employees')}
        description={t(
          'gosi.oh.description',
          'Occupational hazards branch: employer-only contribution covering all employees, including non-Saudis.',
        )}
        summary={data.ohSummary}
        expanded={expanded.oh}
        onToggle={() => toggle('oh')}
        renderRow={renderOhRow}
        renderHeader={renderOhHeader}
        showEmployeeColumn={false}
        t={t}
      />

      {/* Section 3 — SANED */}
      <BranchSection
        sectionKey="saned"
        title={t('gosi.saned.title', 'SANED (Unemployment): Saudi nationals only')}
        description={t(
          'gosi.saned.description',
          'Unemployment insurance for Saudi nationals, 0.75% employer and 0.75% employee on basic + housing.',
        )}
        summary={data.sanedSummary}
        expanded={expanded.saned}
        onToggle={() => toggle('saned')}
        renderRow={renderAniRow /* SANED uses same column set as ANI */}
        renderHeader={renderAniHeader}
        showEmployeeColumn
        t={t}
      />

      {/* Rate schedule (read-only) — collapsible, default collapsed */}
      <Card className="border-blue-100/60 bg-blue-50/50">
        <CardContent className="p-0">
          <button
            type="button"
            className="w-full px-4 py-3 flex items-center justify-between text-start hover:bg-blue-100/50 transition"
            aria-expanded={ratesExpanded}
            onClick={() => setRatesExpanded(v => !v)}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">
                {t('payroll.complianceCenter.gosi.rateScheduleTitle')}
              </h3>
              <Tooltip.Provider>
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <span
                      role="img"
                      aria-label={t('payroll.complianceCenter.gosi.rateScheduleReadOnlyHint')}
                      className="text-muted-foreground hover:text-foreground"
                      onClick={e => e.stopPropagation()}
                    >
                      <Info size={16} />
                    </span>
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content className="bg-card border border-border rounded-md p-2 shadow-md text-xs max-w-xs z-50">
                      {t('payroll.complianceCenter.gosi.rateScheduleReadOnlyHint')}
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
            {ratesExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {ratesExpanded && (
            <div className="px-4 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    {t('payroll.complianceCenter.gosi.rateScheduleTitle')}
                  </caption>
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="text-start py-2 font-medium">
                        {t('payroll.complianceCenter.gosi.columns.year')}
                      </th>
                      <th className="text-start py-2 font-medium">
                        {t('payroll.complianceCenter.gosi.columns.scheme')}
                      </th>
                      <th className="text-start py-2 font-medium">
                        {t('payroll.complianceCenter.gosi.columns.branch')}
                      </th>
                      <th className="text-end py-2 font-medium">
                        {t('payroll.complianceCenter.gosi.columns.employerPct')}
                      </th>
                      <th className="text-end py-2 font-medium">
                        {t('payroll.complianceCenter.gosi.columns.employeePct')}
                      </th>
                      <th className="text-end py-2 font-medium">
                        {t('payroll.complianceCenter.gosi.columns.cap')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.activeRates.map(r => (
                      <tr key={r.id} className="border-b border-border last:border-0">
                        <td className="py-2">{r.year}</td>
                        <td className="py-2">{r.scheme}</td>
                        <td className="py-2">{r.branchType}</td>
                        <td className="py-2 text-end font-mono">
                          {formatRate(r.employerRate)}
                        </td>
                        <td className="py-2 text-end font-mono">
                          {formatRate(r.employeeRate)}
                        </td>
                        <td className="py-2 text-end font-mono">
                          {r.wageCap.toLocaleString('en-US')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Branch section sub-component (kept local to this file per spec)
// ---------------------------------------------------------------------------

type TFunc = (key: string, fallback?: string) => string;

interface BranchSectionProps {
  sectionKey: BranchKey;
  title: string;
  description: string;
  summary: GosiBranchSummary;
  expanded: boolean;
  onToggle: () => void;
  renderRow: (row: GosiCoverageRow) => React.ReactNode;
  renderHeader: (t: TFunc) => React.ReactNode;
  showEmployeeColumn: boolean;
  placeholderNote?: string;
  t: TFunc;
}

const BranchSection: React.FC<BranchSectionProps> = ({
  title,
  description,
  summary,
  expanded,
  onToggle,
  renderRow,
  renderHeader,
  showEmployeeColumn,
  placeholderNote,
  t,
}) => {
  const hasRows = summary.coverage.length > 0;
  return (
    <Card className="border-blue-100/60 bg-blue-50/50">
      <CardContent className="p-0">
        <button
          type="button"
          className="w-full px-4 py-3 flex items-center justify-between text-start hover:bg-blue-100/50 transition rounded-t-lg"
          aria-expanded={expanded}
          onClick={onToggle}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-sm font-semibold">{title}</h3>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-mono">SAR {formatMoney(summary.employerTotal)}</span>
                {showEmployeeColumn && (
                  <>
                    <span>/</span>
                    <span className="font-mono">SAR {formatMoney(summary.employeeTotal)}</span>
                  </>
                )}
                <span>· {summary.coveredEmployeesCount} {t('gosi.panel.employees_short', 'employees')}</span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">{description}</p>
          </div>
          {expanded ? <ChevronUp size={14} className="text-muted-foreground shrink-0" /> : <ChevronDown size={14} className="text-muted-foreground shrink-0" />}
        </button>

        {expanded && (
          <div className="px-4 pb-4">
            {placeholderNote && (
              <div className="text-xs text-muted-foreground italic mb-2">{placeholderNote}</div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">{title}</caption>
                <thead className="text-xs text-muted-foreground">
                  {renderHeader(t)}
                </thead>
                <tbody>
                  {hasRows ? (
                    summary.coverage.map(row => renderRow(row))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-4 text-center text-muted-foreground italic"
                      >
                        {t('gosi.panel.no_employees', '(no employees on this branch)')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Per-branch column renderers
// ---------------------------------------------------------------------------

// ANI / SANED: name | basic | housing | capped base | rate | employer amount | employee amount
const renderAniHeader = (t: TFunc): React.ReactNode => (
  <tr className="border-b border-border">
    <th className="text-start py-2 font-medium">
      {t('payroll.complianceCenter.gosi.columns.name', 'Name')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.basic', 'Basic')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.housing', 'Housing')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.capped_base', 'Capped base')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.rate', 'Rate')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.employer_amount', 'Employer amount')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.employee_amount', 'Employee amount')}
    </th>
  </tr>
);

const renderAniRow = (row: GosiCoverageRow): React.ReactNode => (
  <tr key={row.userId} className="border-b border-border last:border-0">
    <td className="py-2">{row.employeeName}</td>
    <td className="py-2 text-end font-mono">{formatMoney(row.basic)}</td>
    <td className="py-2 text-end font-mono">{formatMoney(row.housing)}</td>
    <td className="py-2 text-end font-mono">{formatMoney(row.cappedBase)}</td>
    <td className="py-2 text-end font-mono">{formatRate(row.employerRate)}</td>
    <td className="py-2 text-end font-mono">{formatMoney(row.employerAmount)}</td>
    <td className="py-2 text-end font-mono">{formatMoney(row.employeeAmount)}</td>
  </tr>
);

// OH: name | nationality | basic | housing | capped base | rate | employer amount
const renderOhHeader = (t: TFunc): React.ReactNode => (
  <tr className="border-b border-border">
    <th className="text-start py-2 font-medium">
      {t('payroll.complianceCenter.gosi.columns.name', 'Name')}
    </th>
    <th className="text-start py-2 font-medium">
      {t('gosi.panel.columns.nationality', 'Nationality')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.basic', 'Basic')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.housing', 'Housing')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.capped_base', 'Capped base')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.rate', 'Rate')}
    </th>
    <th className="text-end py-2 font-medium">
      {t('gosi.panel.columns.employer_amount', 'Employer amount')}
    </th>
  </tr>
);

const renderOhRow = (row: GosiCoverageRow): React.ReactNode => (
  <tr key={row.userId} className="border-b border-border last:border-0">
    <td className="py-2">{row.employeeName}</td>
    <td className="py-2">{formatNationality(row.nationality)}</td>
    <td className="py-2 text-end font-mono">{formatMoney(row.basic)}</td>
    <td className="py-2 text-end font-mono">{formatMoney(row.housing)}</td>
    <td className="py-2 text-end font-mono">{formatMoney(row.cappedBase)}</td>
    <td className="py-2 text-end font-mono">{formatRate(row.employerRate)}</td>
    <td className="py-2 text-end font-mono">{formatMoney(row.employerAmount)}</td>
  </tr>
);
